import { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    num: '01',
    title: 'Inspection & Assessment',
    desc: 'Our certified inspector performs a thorough on-site evaluation — documenting every issue, measuring damage, and identifying root causes before recommending any solution.',
    icon: 'ri-search-eye-line',
  },
  {
    num: '02',
    title: 'Detailed Estimate',
    desc: 'You receive a clear written estimate with no hidden fees, no vague line items, and zero pressure. Just an honest breakdown of exactly what needs to be done.',
    icon: 'ri-file-text-line',
  },
  {
    num: '03',
    title: 'Project Scheduling',
    desc: 'We schedule at your convenience, communicate a precise timeline, and confirm every detail before work begins. Your time is respected throughout.',
    icon: 'ri-calendar-check-line',
  },
  {
    num: '04',
    title: 'Professional Installation',
    desc: 'Our GAF Certified crew installs using premium materials and Fortification-certified techniques built to withstand Gulf Coast weather for decades ahead.',
    icon: 'ri-hammer-line',
  },
  {
    num: '05',
    title: 'Final Walkthrough & Warranty',
    desc: 'We walk through the completed project with you, answer every question, and deliver full warranty documentation before we leave your property.',
    icon: 'ri-shield-check-line',
  },
];

export default function SectionProcess() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="process" ref={ref} className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute right-0 top-1/2 -translate-y-1/2 font-condensed font-bold text-[240px] text-gray-50 leading-none select-none pointer-events-none -mr-12 hidden xl:block">
        HOW
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <div className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-gold"></span>
            <span className="text-[11px] font-bold tracking-super uppercase text-gold">How It Works</span>
          </div>
          <h2 className="font-condensed font-bold text-5xl md:text-6xl text-charcoal uppercase leading-none">
            A Straightforward<br />
            <span className="font-serif font-normal italic text-gray-400 text-4xl md:text-5xl normal-case leading-tight">
              Roofing Process
            </span>
          </h2>
        </div>
        <div className="relative max-w-3xl">
          <div className="absolute left-5 top-10 bottom-10 w-px bg-gradient-to-b from-gold/50 via-gold/20 to-transparent hidden sm:block"></div>
          <div className="flex flex-col gap-0">
            {STEPS.map((step, i) => (
              <div key={step.num}
                className={`flex gap-6 md:gap-10 p-5 md:p-6 rounded-xl group hover:bg-gray-50 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 110}ms`, transitionDuration: '700ms' }}>
                <div className="shrink-0">
                  <div className="w-10 h-10 flex items-center justify-center border-2 border-gold/30 bg-white rounded-full group-hover:border-gold group-hover:bg-gold/8 transition-all relative z-10">
                    <i className={`${step.icon} text-gold text-xs`}></i>
                  </div>
                </div>
                <div className="flex-1 pt-1.5 pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-condensed font-bold text-4xl text-gray-100 leading-none select-none group-hover:text-gold/12 transition-colors">
                      {step.num}
                    </span>
                    <h3 className="font-condensed font-bold text-lg text-charcoal uppercase tracking-wide">{step.title}</h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed font-light max-w-2xl">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={`mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-serif italic text-gray-400 text-lg">&ldquo;From inspection to finished roof in under a week.&rdquo;</p>
          <a href="#estimate" onClick={e => { e.preventDefault(); document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-3 bg-gold text-ink font-bold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap shrink-0">
            Start the Process
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
